class RSCError extends Error {
  constructor(message: string) {
    super(`[snap-slider]: ${message}`);
  }
}

export const getOffsetRect = (el: Element, relativeTo?: Element | null) => {
  const rect = _getOffsetRect(el);

  if (!relativeTo) {
    return rect;
  }

  const relativeRect = _getOffsetRect(relativeTo);

  return {
    left: rect.left - relativeRect.left,
    top: rect.top - relativeRect.top,
    right: rect.right - relativeRect.left,
    bottom: rect.bottom - relativeRect.top,
    width: rect.width,
    height: rect.height,
  };
};

const _getOffsetRect = (el: Element) => {
  const rect = el.getBoundingClientRect();

  let scrollLeft = 0;
  let scrollTop = 0;
  let parentEl = el.parentElement;

  while (parentEl) {
    scrollLeft += parentEl.scrollLeft;
    scrollTop += parentEl.scrollTop;
    parentEl = parentEl.parentElement;
  }

  const left = rect.left + scrollLeft;
  const top = rect.top + scrollTop;

  return {
    left: left,
    top: top,
    right: left + rect.width,
    bottom: top + rect.height,
    width: rect.width,
    height: rect.height,
  };
};

export const getScrollPaddingUsedValue = (
  el: HTMLElement,
  pos: 'left' | 'top'
) => {
  if (!el) {
    throw new RSCError('Element is null or undefined');
  }

  if (!(el instanceof Element)) {
    throw new RSCError('Parameter is not of type Element');
  }

  const style = window.getComputedStyle(el);
  const scrollPadding = style.getPropertyValue(`scroll-padding-${pos}`);

  if (scrollPadding === 'auto') {
    return 0;
  }

  const invalidMsg = `Unsupported scroll padding value, expected <length> or <percentage> value, received ${scrollPadding}`;

  if (scrollPadding.endsWith('px')) {
    const value = parseInt(scrollPadding);
    assert(!Number.isNaN(value), invalidMsg);
    return value;
  }

  if (scrollPadding.endsWith('%')) {
    const value = parseInt(scrollPadding);
    assert(!Number.isNaN(value), invalidMsg);
    return (el.clientWidth / 100) * value;
  }

  throw new RSCError(invalidMsg);
};

export const getScrollMarginUsedValue = (
  el: HTMLElement,
  pos: 'left' | 'top'
) => {
  if (!el) {
    throw new RSCError('Element is null or undefined');
  }

  if (!(el instanceof Element)) {
    throw new RSCError('Parameter is not of type Element');
  }

  const style = window.getComputedStyle(el);

  const scrollMargin = style.getPropertyValue(`scroll-margin-${pos}`);

  const invalidMsg = `Unsupported scroll margin value, expected <length> value, received ${scrollMargin}`;

  assert(scrollMargin.endsWith('px'), invalidMsg);

  const value = parseInt(scrollMargin);

  assert(!Number.isNaN(value), invalidMsg);

  return value;
};

export const getEffectiveScrollSpacing = (
  scrollEl: HTMLElement,
  itemEl: HTMLElement,
  pos: 'left' | 'top'
) => {
  const scrollPadding = getScrollPaddingUsedValue(scrollEl, pos);

  const scrollMargin = getScrollMarginUsedValue(itemEl, pos);

  const rect = getOffsetRect(itemEl, itemEl.parentElement);

  return Math.min(scrollPadding + scrollMargin, rect[pos]);
};

export function assert(value: any, message: string): asserts value {
  if (value) {
    return;
  }

  console.log('Assert error', message);
  throw new RSCError(message);
}
